import { useState } from 'react';
import { ConnectAgencyForm, ConnectSubAccountForm } from '../components/ConnectForms';

type Method = 'subaccount' | 'agency' | 'oauth';

/**
 * Two ways in, and the token path is the default.
 *
 * HighLevel splits permissions by level: listing and creating sub-accounts are
 * agency-only, while calls, appointments, contacts, users and calendars are all
 * sub-account-level. A Private Integration Token is issued at one level, so the
 * dashboard accepts one per level and uses whichever it has.
 */
export function Connect({
  onConnected,
  oauthConfigured,
}: {
  onConnected: () => void;
  oauthConfigured: boolean;
}) {
  const [method, setMethod] = useState<Method>('subaccount');

  return (
    <div className="centered">
      <div className="panel" style={{ width: 'min(760px, 100%)' }}>
        <div className="card stack">
          <h1>Connect HighLevel</h1>
          <p className="muted">
            Paste a Private Integration Token and this dashboard starts reading. No marketplace app,
            no developer account, no OAuth redirect to host.
          </p>

          <div className="chips">
            <button
              type="button"
              className="chip"
              aria-pressed={method === 'subaccount'}
              onClick={() => setMethod('subaccount')}
            >
              {method === 'subaccount' ? '✓ ' : ''}A sub-account
            </button>
            <button
              type="button"
              className="chip"
              aria-pressed={method === 'agency'}
              onClick={() => setMethod('agency')}
            >
              {method === 'agency' ? '✓ ' : ''}My agency
            </button>
            <button
              type="button"
              className="chip"
              aria-pressed={method === 'oauth'}
              onClick={() => setMethod('oauth')}
            >
              {method === 'oauth' ? '✓ ' : ''}Marketplace app
            </button>
          </div>
        </div>

        {method === 'subaccount' ? (
          <div className="card stack">
            <h2>Connect a sub-account</h2>
            <p className="card-sub">
              Everything this dashboard reports on — calls, appointments, contacts, users, calendars —
              is sub-account-level in HighLevel, so one token per sub-account is all it needs. Add as
              many as you want; repeat this for each one.
            </p>
            <ConnectSubAccountForm onConnected={onConnected} />
          </div>
        ) : null}

        {method === 'agency' ? (
          <div className="card stack">
            <h2>Connect your agency</h2>
            <p className="card-sub">
              An agency token adds the two things a sub-account token cannot do: discovering your
              sub-accounts automatically, and creating a new one from this dashboard. It does not
              replace sub-account tokens — HighLevel does not let an agency token read a
              sub-account's calls directly, so add a token per sub-account as well.
            </p>
            <ConnectAgencyForm onConnected={onConnected} />
          </div>
        ) : null}

        {method === 'oauth' ? (
          <div className="card stack">
            <h2>Install the marketplace app</h2>
            <p className="card-sub">
              The alternative to pasting tokens. One agency-level install covers every sub-account,
              because the app exchanges its agency token for a sub-account token per location. It
              needs a developer account, a registered app, and a public redirect URL — worth it for
              many sub-accounts, overkill for a few.
            </p>
            <p className="small muted">
              Set the app's distribution so an agency installs it but it is <em>used</em> at
              sub-account level, then give it both the agency scopes (locations.readonly,
              locations.write, oauth.readonly, oauth.write) and the sub-account scopes
              (conversations.readonly, conversations/message.readonly, contacts.readonly,
              users.readonly, calendars.readonly, calendars/events.readonly). If your app's scope
              picker will not offer both sets, use the token method above instead — it reaches the
              same data.
            </p>
            {oauthConfigured ? (
              <a className="btn btn-primary" href="/api/auth/install" style={{ textAlign: 'center' }}>
                Install on my agency
              </a>
            ) : (
              <div className="banner">
                <span>
                  Not configured. Set <code>GHL_CLIENT_ID</code>, <code>GHL_CLIENT_SECRET</code> and{' '}
                  <code>GHL_REDIRECT_URI</code> in <code>.env</code> and restart to enable this. The
                  token methods need none of them.
                </span>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
